import { Avatar, Column, Timestamp } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ReadSearchApiResponse, SearchResultEnriched } from 'lib-search-types';

import { getUserDisplayName } from 'utils/user';

import styles from '../../../../../searchResults.module.scss';
import { SearchResultsPagination } from '../../../../pagination/searchResultsPagination';
import { SearchResult } from '../../../../result/searchResult';

type UserResult = SearchResultEnriched<'users'>;
type CompanyResult = SearchResultEnriched<'companies'>;

type ContactResultProps = {
  results: readonly UserResult[] | readonly CompanyResult[];
  meta?: ReadSearchApiResponse['meta'];
  selectedId?: string;
  onSelectResult?: (hit: UserResult | CompanyResult) => void;
};

export const ContactResult = ({
  results,
  meta,
  selectedId,
  onSelectResult,
}: ContactResultProps): JSX.Element => {
  const renderUserResult = (result: UserResult) => {
    const { result: user } = result;

    const separator = ' · ';

    const metadata =
      user.email && user.phone_number ? (
        <span>
          {user.email} {separator} {user.phone_number}
        </span>
      ) : (
        user.email || user.phone_number || ''
      );

    return (
      <SearchResult
        key={user.id}
        className={classnames({
          [styles.rowClickable]: true,
          [styles.rowSelected]: user.id === selectedId,
        })}
        content={{
          title: getUserDisplayName(user),
          message: (
            <span>
              {metadata} {metadata && separator}
              <span>
                {'Created '}
                <Timestamp relative time={user.created_at ?? ''} />
              </span>
            </span>
          ),
          avatar: <Avatar user={user} />,
        }}
        onClick={() => onSelectResult?.(result)}
      />
    );
  };

  const renderCompanyResult = (result: CompanyResult) => {
    const { result: company } = result;

    return (
      <SearchResult
        key={company.id}
        className={classnames({
          [styles.rowClickable]: true,
          [styles.rowSelected]: company.id === selectedId,
        })}
        content={{
          title: company.name,
          message: (
            <span>
              {'Created '}
              <Timestamp relative time={company.created_at} />
            </span>
          ),
          avatar: <Avatar user={{ name: company.name }} />,
        }}
        onClick={() => onSelectResult?.(result)}
      />
    );
  };

  return (
    <>
      <Column.Row scrollable noVerticalPadding noHorizontalPadding>
        {results.map((result) => {
          if ('type' in result.result) {
            return renderUserResult(result as UserResult);
          }
          return renderCompanyResult(result as CompanyResult);
        })}
      </Column.Row>

      <Column.Row noVerticalPadding noHorizontalPadding borderTop>
        <SearchResultsPagination
          pageCount={meta?.total_pages ?? 1}
          currentPage={meta?.page ?? 1}
        />
      </Column.Row>
    </>
  );
};
